import { createClient } from './http';
import type { MapSettings } from '../admin/types';

const MAPS_API_BASE = import.meta.env.VITE_MAPS_API_BASE_URL ?? 'https://x-gis.xenus.dev/api/maps';

const request = createClient(MAPS_API_BASE);

type BehaviorDto = {
  remember_user_position: boolean;
  restrict_pan_to_extent: boolean;
  auto_open_legend: boolean;
  persist_active_layers: boolean;
  show_cursor_coordinates: boolean;
};

type ToolsDto = {
  search: boolean;
  legend: boolean;
  share: boolean;
  box_select: boolean;
  measure: boolean;
  locate: boolean;
  coordinates: boolean;
  edit_features: boolean;
  spatial_query: boolean;
  clustering: boolean;
  object_calculator: boolean;
  export_selection: boolean;
  print_map: boolean;
  ai_analysis: boolean;
  rosreestr: boolean;
};

type SearchDto = {
  search_objects: boolean;
  search_addresses: boolean;
  min_query_length: number;
  max_results: number;
  fly_to_on_select: boolean;
};

type LegendDto = {
  group_by_layer_group: boolean;
  show_object_counts: boolean;
  show_opacity_slider: boolean;
};

type MapDto = {
  id: string;
  title: string;
  code: string;
  project: string;
  crs: string;
  default_basemap_name: string;
  initial_scale_label: string;
  zoom_min: number;
  zoom_max: number;
  center_lat: number;
  center_lon: number;
  zoom: number;
  min_lat: number;
  min_lon: number;
  max_lat: number;
  max_lon: number;
  behavior: BehaviorDto;
  tools: ToolsDto;
  search: SearchDto;
  legend: LegendDto;
  default_panel: string;
};

export type GeoserverInfo = {
  connected: boolean;
  workspace: string;
  ows_url: string;
};

export type EmbedConfig = {
  showLegend: boolean;
  showBasemapSwitcher: boolean;
  width: number;
  height: number;
};

export type PublicLink = {
  token: string;
  isActive: boolean;
  embedConfig: EmbedConfig;
};

type EmbedConfigDto = {
  show_legend: boolean;
  show_basemap_switcher: boolean;
  width: number;
  height: number;
};

type PublicLinkDto = {
  token: string;
  is_active: boolean;
  embed_config: EmbedConfigDto;
  created_at: string;
  updated_at: string;
};

function embedConfigToPayload(cfg: EmbedConfig): EmbedConfigDto {
  return {
    show_legend: cfg.showLegend,
    show_basemap_switcher: cfg.showBasemapSwitcher,
    width: cfg.width,
    height: cfg.height,
  };
}

function publicLinkFromDto(dto: PublicLinkDto): PublicLink {
  return {
    token: dto.token,
    isActive: dto.is_active,
    embedConfig: {
      showLegend: dto.embed_config.show_legend,
      showBasemapSwitcher: dto.embed_config.show_basemap_switcher,
      width: dto.embed_config.width,
      height: dto.embed_config.height,
    },
  };
}

function toPayload(settings: MapSettings) {
  return {
    title: settings.title,
    code: settings.code,
    project: settings.project,
    crs: settings.crs,
    default_basemap_name: settings.defaultBasemapName,
    initial_scale_label: settings.initialScaleLabel,
    zoom_min: settings.zoomRange[0],
    zoom_max: settings.zoomRange[1],
    center_lat: settings.initialView.centerLat,
    center_lon: settings.initialView.centerLon,
    zoom: settings.initialView.zoom,
    min_lat: settings.initialView.minLat,
    min_lon: settings.initialView.minLon,
    max_lat: settings.initialView.maxLat,
    max_lon: settings.initialView.maxLon,
    behavior: {
      remember_user_position: settings.behavior.rememberUserPosition,
      restrict_pan_to_extent: settings.behavior.restrictPanToExtent,
      auto_open_legend: settings.behavior.autoOpenLegend,
      persist_active_layers: settings.behavior.persistActiveLayers,
      show_cursor_coordinates: settings.behavior.showCursorCoordinates,
    },
    tools: {
      search: settings.tools.search,
      legend: settings.tools.legend,
      share: settings.tools.share,
      box_select: settings.tools.boxSelect,
      measure: settings.tools.measure,
      locate: settings.tools.locate,
      coordinates: settings.tools.coordinates,
      edit_features: settings.tools.editFeatures,
      spatial_query: settings.tools.spatialQuery,
      clustering: settings.tools.clustering,
      object_calculator: settings.tools.objectCalculator,
      export_selection: settings.tools.exportSelection,
      print_map: settings.tools.printMap,
      ai_analysis: settings.tools.aiAnalysis,
      rosreestr: settings.tools.rosreestr,
    },
    search: {
      search_objects: settings.search.searchObjects,
      search_addresses: settings.search.searchAddresses,
      min_query_length: settings.search.minQueryLength,
      max_results: settings.search.maxResults,
      fly_to_on_select: settings.search.flyToOnSelect,
    },
    legend: {
      group_by_layer_group: settings.legend.groupByLayerGroup,
      show_object_counts: settings.legend.showObjectCounts,
      show_opacity_slider: settings.legend.showOpacitySlider,
    },
    default_panel: settings.defaultPanel,
  };
}

function fromDto(dto: MapDto): MapSettings {
  return {
    id: dto.id,
    title: dto.title,
    code: dto.code,
    project: dto.project,
    crs: dto.crs,
    defaultBasemapName: dto.default_basemap_name,
    initialScaleLabel: dto.initial_scale_label,
    zoomRange: [dto.zoom_min, dto.zoom_max],
    behavior: {
      rememberUserPosition: dto.behavior.remember_user_position,
      restrictPanToExtent: dto.behavior.restrict_pan_to_extent,
      autoOpenLegend: dto.behavior.auto_open_legend,
      persistActiveLayers: dto.behavior.persist_active_layers,
      showCursorCoordinates: dto.behavior.show_cursor_coordinates,
    },
    initialView: {
      centerLat: dto.center_lat,
      centerLon: dto.center_lon,
      zoom: dto.zoom,
      minLat: dto.min_lat,
      minLon: dto.min_lon,
      maxLat: dto.max_lat,
      maxLon: dto.max_lon,
    },
    tools: {
      search: dto.tools.search,
      legend: dto.tools.legend,
      share: dto.tools.share,
      boxSelect: dto.tools.box_select,
      measure: dto.tools.measure,
      locate: dto.tools.locate,
      coordinates: dto.tools.coordinates,
      editFeatures: dto.tools.edit_features,
      spatialQuery: dto.tools.spatial_query,
      clustering: dto.tools.clustering,
      objectCalculator: dto.tools.object_calculator,
      exportSelection: dto.tools.export_selection,
      printMap: dto.tools.print_map,
      aiAnalysis: dto.tools.ai_analysis,
      rosreestr: dto.tools.rosreestr,
    },
    search: {
      searchObjects: dto.search.search_objects,
      searchAddresses: dto.search.search_addresses,
      minQueryLength: dto.search.min_query_length,
      maxResults: dto.search.max_results,
      flyToOnSelect: dto.search.fly_to_on_select,
    },
    legend: {
      groupByLayerGroup: dto.legend.group_by_layer_group,
      showObjectCounts: dto.legend.show_object_counts,
      showOpacitySlider: dto.legend.show_opacity_slider,
    },
    defaultPanel: dto.default_panel as MapSettings['defaultPanel'],
  };
}

export const mapsApi = {
  listMaps: async (): Promise<MapSettings[]> => {
    const rows = await request<MapDto[]>('/maps', {}, true);
    return rows.map(fromDto);
  },

  createMap: async (settings: MapSettings): Promise<MapSettings> => {
    const dto = await request<MapDto>('/maps', { method: 'POST', body: JSON.stringify(toPayload(settings)) }, true);
    return fromDto(dto);
  },

  updateMap: async (id: string, settings: MapSettings): Promise<MapSettings> => {
    const dto = await request<MapDto>(`/maps/${id}`, { method: 'PATCH', body: JSON.stringify(toPayload(settings)) }, true);
    return fromDto(dto);
  },

  getGeoserverInfo: () => request<GeoserverInfo>('/geoserver/info', {}, true),

  /** 404 (via ApiError.status) means public access has never been turned on
   * for this map — that's a normal state, not an error, callers should catch
   * it and show the "enable" button rather than surfacing it. */
  getPublicLink: async (mapId: string): Promise<PublicLink> => {
    const dto = await request<PublicLinkDto>(`/maps/${mapId}/public-link`, {}, true);
    return publicLinkFromDto(dto);
  },

  /** Idempotent — creates the link the first time, just reactivates it (same
   * token) every time after. */
  enablePublicLink: async (mapId: string): Promise<PublicLink> => {
    const dto = await request<PublicLinkDto>(`/maps/${mapId}/public-link`, { method: 'POST' }, true);
    return publicLinkFromDto(dto);
  },

  /** Issues a brand-new token — every embed using the old one stops working. */
  regeneratePublicLink: async (mapId: string): Promise<PublicLink> => {
    const dto = await request<PublicLinkDto>(`/maps/${mapId}/public-link/regenerate`, { method: 'POST' }, true);
    return publicLinkFromDto(dto);
  },

  updatePublicLink: async (mapId: string, patch: { isActive?: boolean; embedConfig?: EmbedConfig }): Promise<PublicLink> => {
    const dto = await request<PublicLinkDto>(
      `/maps/${mapId}/public-link`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          is_active: patch.isActive,
          embed_config: patch.embedConfig ? embedConfigToPayload(patch.embedConfig) : undefined,
        }),
      },
      true,
    );
    return publicLinkFromDto(dto);
  },
};
